/**
 * STAGE-1B BROWSER VALIDATOR EXTRACTION
 * Non-active browser-safe Zod validation schemas.
 * shared/schema.ts remains authoritative.
 * DO NOT import directly yet.
 *
 * EXTRACTION CRITERIA:
 * Only pure z.object() / z.* schemas that have NO dependency on:
 * - pgTable() definitions (Drizzle tables)
 * - createInsertSchema() / createSelectSchema() (drizzle-zod)
 * - relations() / drizzle-orm utilities
 *
 * STAGE-1A EXTRACTED (8 schemas):
 * 1. pestAnalysisResponseSchema       — AI pest analysis response shape
 * 2. createWorkOrderFromAnalysisSchema — Work order creation from analysis
 * 3. createRoleRequestSchema           — RBAC role creation request
 * 4. assignUserRoleRequestSchema       — RBAC role assignment request
 * 5. grantPermissionRequestSchema      — RBAC permission grant request
 * 6. checkPermissionRequestSchema      — RBAC permission check request
 * 7. effectivePermissionsResponseSchema — RBAC effective permissions response
 * 8. permissionCheckResponseSchema     — RBAC permission check response
 *
 * STAGE-1B EXTRACTED (18 additional schemas):
 * Auth:
 *  9. loginRequestSchema               — Login form DTO
 * 10. registerRequestSchema            — Registration form DTO (no passwordHash)
 * 11. changePasswordRequestSchema      — Password change form DTO
 * 12. userProfileDisplaySchema         — Safe user profile read DTO
 * 13. updateUserProfileFormSchema      — Profile update form (no sensitive fields)
 * Customers:
 * 14. createCustomerFormSchema         — Customer creation (no billing IDs)
 * 15. customerSearchSchema             — Customer search/filter DTO
 * CRM:
 * 16. createContactFormSchema          — Public contact submission form
 * 17. createCrmContactFormSchema       — Internal CRM contact form
 * 18. createLeadFormSchema             — Lead creation form
 * Service:
 * 19. createServiceOrderFormSchema     — Service order creation form
 * 20. bookAppointmentFormSchema        — Appointment booking form
 * 21. createQuoteFormSchema            — Quote request form (no estimatedCost)
 * 22. serviceOrderFilterSchema         — Service order list filter DTO
 * Operations:
 * 23. createAddressFormSchema          — Address entry form (no geocoding fields)
 * 24. createTaskFormSchema             — Task creation form
 * Pricing:
 * 25. pricingPlanDisplaySchema         — Pricing plan display DTO
 * Billing:
 * 26. invoiceLineItemFormSchema        — Invoice line item form
 * Pest ID:
 * 27. createPestIdentificationFormSchema — Pest identification submission form (Stage-3A)
 *
 * NOTE: Schemas using z.any() are safe for browser use. z.any() is a pure
 * Zod construct with no Drizzle dependency.
 */
import { z } from "zod";

// ============================================================
// PEST ANALYSIS SCHEMAS
// ============================================================

// Analysis Response Schema — describes the structure of an AI pest analysis
// Mirrors: PestAnalysisResponse type in shared/schema.ts
export const pestAnalysisResponseSchema = z.object({
  id: z.number(),
  pest_type: z.string(),
  scientific_name: z.string().optional(),
  confidence: z.number().min(0).max(1),
  severity: z.enum(["low", "medium", "high", "critical"]),
  lifecycle_stage: z.string().optional(),
  characteristics_identified: z.array(z.string()).optional(),
  recommended_actions: z.array(
    z.object({
      action: z.string(),
      description: z.string(),
      priority: z.enum(["high", "medium", "low"]),
      estimatedCost: z.number().optional(),
      materials: z.array(z.string()).optional(),
      timeframe: z.string().optional(),
    }),
  ),
  related_services: z.array(
    z.object({
      serviceType: z.enum(["pest_control", "hygiene"]),
      description: z.string(),
      estimatedCost: z.number(),
      priority: z.enum(["high", "medium", "low"]),
    }),
  ),
  processing_time_ms: z.number().optional(),
  created_at: z.string().datetime(),
});

// Work Order Creation from Analysis Schema
export const createWorkOrderFromAnalysisSchema = z.object({
  analysisRequestId: z.number(),
  serviceType: z.enum(["pest_control", "hygiene"]),
  selectedRecommendations: z.array(z.number()), // IDs of selected recommendations
  customerInfo: z
    .object({
      name: z.string(),
      email: z.string().email(),
      phone: z.string(),
      address: z.string(),
      postcode: z.string(),
    })
    .optional(),
  scheduledDate: z.string().datetime().optional(),
  notes: z.string().optional(),
});

// ============================================================
// RBAC REQUEST / RESPONSE SCHEMAS
// ============================================================

// Request schema for role management API
export const createRoleRequestSchema = z.object({
  name: z.string().min(1).max(100),
  code: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[a-z_]+$/), // Only lowercase and underscores
  description: z.string().optional(),
  level: z.number().int().min(1).max(10),
  parentRoleId: z.number().optional(),
  inheritsPermissions: z.boolean().default(true),
  maxUsers: z.number().int().positive().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#3B82F6"),
  icon: z.string().optional(),
  permissions: z.array(z.number()).default([]), // Array of permission IDs to assign
});

export const assignUserRoleRequestSchema = z.object({
  userId: z.string().min(1),
  roleIds: z.array(z.number()).min(1), // Array of role IDs to assign
  isPrimary: z.boolean().optional(), // Which role should be primary
  reason: z.string().optional(),
  expiresAt: z.string().datetime().optional(),
  isTemporary: z.boolean().default(false),
  temporaryContext: z.string().optional(),
});

export const grantPermissionRequestSchema = z.object({
  roleId: z.number(),
  permissionIds: z.array(z.number()).min(1),
  conditions: z
    .object({
      brandRestriction: z.array(z.string()).optional(),
      resourceFilter: z.string().optional(),
      timeRestriction: z
        .object({
          startTime: z.string().optional(),
          endTime: z.string().optional(),
        })
        .optional(),
      locationRestriction: z.array(z.string()).optional(),
      customConditions: z.any().optional(),
    })
    .optional(),
  reason: z.string().optional(),
  expiresAt: z.string().datetime().optional(),
});

// Permission checking request schema
export const checkPermissionRequestSchema = z.object({
  userId: z.string(),
  permissionCode: z.string(),
  brand: z.enum(["aegis360", "greenshield"]).optional(),
  context: z
    .object({
      resourceId: z.string().optional(),
      location: z.string().optional(),
      timestamp: z.string().datetime().optional(),
    })
    .optional(),
});

// Response schemas
export const effectivePermissionsResponseSchema = z.object({
  userId: z.string(),
  brand: z.string(),
  roles: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      level: z.number(),
      isPrimary: z.boolean(),
      isActive: z.boolean(),
      expiresAt: z.string().datetime().nullable(),
    }),
  ),
  permissions: z.array(
    z.object({
      id: z.number(),
      code: z.string(),
      module: z.string(),
      action: z.string(),
      resource: z.string().nullable(),
      description: z.string(),
      category: z.string(),
      grantedVia: z.array(z.string()), // Array of role names that grant this permission
      conditions: z.any().optional(),
      expiresAt: z.string().datetime().nullable(),
    }),
  ),
  hasConflicts: z.boolean(),
  conflicts: z.array(z.string()).optional(),
});

export const permissionCheckResponseSchema = z.object({
  userId: z.string(),
  permissionCode: z.string(),
  hasPermission: z.boolean(),
  grantedBy: z.array(z.string()).optional(), // Roles that grant this permission
  deniedReason: z.string().optional(),
  conditions: z.any().optional(),
  cacheHit: z.boolean().optional(),
});

// ============================================================
// STAGE-1B: AUTH VALIDATORS
// ============================================================

/** Login form DTO — no sensitive fields */
export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  brand: z.enum(["aegis360", "greenshield"]),
  rememberMe: z.boolean().optional(),
});

/** Registration form DTO — no passwordHash, no internal flags */
export const registerRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  confirmPassword: z.string().min(8),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  phone: z.string().optional(),
  company: z.string().optional(),
  brand: z.enum(["aegis360", "greenshield"]),
});

/** Password change form DTO — confirmNewPassword is browser-only */
export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8),
  confirmNewPassword: z.string().min(8),
});

/**
 * Safe user profile read DTO.
 * Omits: passwordHash, mustResetPassword, canInviteUsers, isOrgAdmin, companyId
 */
export const userProfileDisplaySchema = z.object({
  id: z.string(),
  email: z.string().email().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  profileImageUrl: z.string().url().optional(),
  company: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  brand: z.string(),
  role: z.string(),
  createdAt: z.string().datetime().optional(),
});

/**
 * Profile update form DTO.
 * Omits: passwordHash, mustResetPassword, canInviteUsers, isOrgAdmin, companyId, email, role, brand
 */
export const updateUserProfileFormSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  phone: z.string().optional(),
  profileImageUrl: z.string().url().optional(),
  company: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
});

// ============================================================
// STAGE-1B: CUSTOMER VALIDATORS
// ============================================================

/**
 * Customer creation form DTO.
 * Omits: stripeCustomerId, quickbooksId, encrypted fields, totalOrders, totalSpent,
 *        lastServiceDate, customerSince, isPilotPartner, onboardingStatus
 */
export const createCustomerFormSchema = z.object({
  name: z.string().min(1),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  postcode: z.string().optional(),
  propertyType: z.string().optional(),
  notes: z.string().optional(),
  status: z.enum(["active", "inactive", "blocked"]).default("active"),
  brand: z.enum(["aegis360", "greenshield"]),
  customerType: z
    .enum(["individual", "business", "corporate"])
    .default("individual"),
  businessName: z.string().optional(),
  vatNumber: z.string().optional(),
  companyRegistrationNumber: z.string().optional(),
  businessType: z
    .enum(["limited_company", "sole_trader", "partnership", "llp"])
    .optional(),
  preferredContactMethod: z.enum(["email", "phone", "sms"]).optional(),
  marketingOptIn: z.boolean().default(false),
  reminderOptIn: z.boolean().default(true),
});

/** Customer search/filter query DTO */
export const customerSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  customerType: z.string().optional(),
  brand: z.enum(["aegis360", "greenshield"]).optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});

// ============================================================
// STAGE-1B: CRM VALIDATORS
// ============================================================

/**
 * Public contact submission form DTO.
 * Omits: id, status (server-set to "new"), createdAt
 */
export const createContactFormSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  service: z.string().optional(),
  message: z.string().min(1).max(2000),
  source: z.enum(["website", "referral", "phone", "email"]).default("website"),
  brand: z.enum(["aegis360", "greenshield"]),
});

/**
 * Internal CRM contact creation form DTO.
 * Omits: id, status, lastContactDate, totalInteractions, createdAt, updatedAt
 */
export const createCrmContactFormSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  mobile: z.string().optional(),
  directLine: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  customerId: z.number().int().positive().optional(),
  supplierId: z.number().int().positive().optional(),
  entityType: z.enum(["customer", "supplier"]),
  role: z.enum(["primary", "secondary", "billing", "technical", "emergency"]),
  isPrimaryContact: z.boolean().default(false),
  decisionMaker: z.boolean().default(false),
  preferredContactMethod: z.enum(["email", "phone", "mobile"]).optional(),
  bestTimeToContact: z.enum(["morning", "afternoon", "evening"]).optional(),
  timeZone: z.string().default("Europe/London"),
  marketingOptIn: z.boolean().default(false),
  linkedInProfile: z.string().url().optional(),
  notes: z.string().optional(),
  brand: z.enum(["aegis360", "greenshield"]),
});

/**
 * Lead creation form DTO.
 * Omits: id, createdAt, updatedAt
 */
export const createLeadFormSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  source: z.enum(["website", "referral", "phone", "email"]),
  status: z
    .enum(["new", "contacted", "qualified", "converted", "lost"])
    .default("new"),
  priority: z.enum(["low", "medium", "high"]).default("medium"),
  assignedTo: z.string().optional(),
  notes: z.string().optional(),
  followUpDate: z.string().datetime().optional(),
  customerId: z.number().int().positive().optional(),
  brand: z.enum(["aegis360", "greenshield"]),
});

// ============================================================
// STAGE-1B: SERVICE VALIDATORS
// ============================================================

/**
 * Service order creation form DTO.
 * Omits: id, status, totalAmount, paymentStatus, completedDate, createdAt, updatedAt
 */
export const createServiceOrderFormSchema = z.object({
  customerId: z.number().int().positive(),
  serviceId: z.number().int().positive(),
  appointmentId: z.number().int().positive().optional(),
  serviceType: z
    .enum(["pest_control", "hygiene", "both"])
    .default("pest_control"),
  notes: z.string().optional(),
  scheduledDate: z.string().datetime().optional(),
  brand: z.enum(["aegis360", "greenshield"]),
});

/**
 * Appointment booking form DTO.
 * Omits: id, status, riskLevel, urgency, estimatedCost, pestIdentificationId, identifiedPest, createdAt
 */
export const bookAppointmentFormSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(1),
  address: z.string().min(1),
  postcode: z.string().min(1),
  serviceType: z.string().min(1),
  appointmentDate: z.string().datetime(),
  description: z.string().optional(),
  propertyType: z.string().optional(),
  source: z.enum(["website", "ar-tool", "phone", "referral"]).optional(),
  brand: z.enum(["aegis360", "greenshield"]),
});

/**
 * Quote request form DTO.
 * Omits: id, estimatedCost (server-calculated), status (server-set), createdAt
 */
export const createQuoteFormSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(1),
  address: z.string().min(1),
  postcode: z.string().min(1),
  propertyType: z.string().min(1),
  propertySize: z.string().min(1),
  propertyAge: z.string().optional(),
  accessDetails: z.string().optional(),
  pestTypes: z.array(z.string()).min(1),
  severity: z.string().min(1),
  previousTreatments: z.boolean().default(false),
  previousTreatmentDetails: z.string().optional(),
  urgency: z.string().min(1),
  preferredDate: z.string().optional(),
  timePreference: z.string().optional(),
  serviceType: z.string().min(1),
  budgetRange: z.string().optional(),
  description: z.string().optional(),
  specialRequirements: z.string().optional(),
  petsSafetyNeeded: z.boolean().default(false),
  childrenSafetyNeeded: z.boolean().default(false),
  organicPreference: z.boolean().default(false),
  contactMethod: z.string().min(1),
  marketingConsent: z.boolean().default(false),
  brand: z.enum(["aegis360", "greenshield"]),
});

/** Service order list filter/search DTO */
export const serviceOrderFilterSchema = z.object({
  status: z
    .enum(["pending", "scheduled", "in-progress", "completed", "cancelled"])
    .optional(),
  serviceType: z.enum(["pest_control", "hygiene", "both"]).optional(),
  brand: z.enum(["aegis360", "greenshield"]).optional(),
  fromDate: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});

// ============================================================
// STAGE-1B: OPERATIONS VALIDATORS
// ============================================================

/**
 * Address entry form DTO.
 * Omits: id, latitude, longitude (server-geocoded), isValidated, validatedAt,
 *        lastServiceDate, nextServiceDate, status (server-set), createdAt, updatedAt
 */
export const createAddressFormSchema = z.object({
  customerId: z.number().int().positive().optional(),
  supplierId: z.number().int().positive().optional(),
  entityType: z.enum(["customer", "supplier"]),
  addressType: z.enum([
    "billing",
    "service",
    "delivery",
    "head_office",
    "warehouse",
  ]),
  isPrimary: z.boolean().default(false),
  addressLine1: z.string().min(1),
  addressLine2: z.string().optional(),
  city: z.string().min(1),
  county: z.string().optional(),
  postcode: z.string().min(1),
  country: z.string().default("United Kingdom"),
  propertyType: z.string().optional(),
  propertySize: z.string().optional(),
  accessNotes: z.string().optional(),
  safetyNotes: z.string().optional(),
  serviceFrequency: z
    .enum(["weekly", "monthly", "quarterly", "annual", "one_off"])
    .optional(),
  notes: z.string().optional(),
  brand: z.enum(["aegis360", "greenshield"]),
});

/**
 * Task creation form DTO.
 * Omits: id, completedDate (server-set), createdAt, updatedAt
 */
export const createTaskFormSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  type: z.enum(["follow_up", "service", "inspection", "quote"]),
  priority: z.enum(["low", "medium", "high"]).default("medium"),
  status: z
    .enum(["pending", "in_progress", "completed", "cancelled"])
    .default("pending"),
  assignedTo: z.string().optional(),
  customerId: z.number().int().positive().optional(),
  leadId: z.number().int().positive().optional(),
  dueDate: z.string().datetime().optional(),
  brand: z.enum(["aegis360", "greenshield"]),
});

// ============================================================
// STAGE-1B: PRICING VALIDATORS
// ============================================================

/**
 * Pricing plan display DTO.
 * Omits: isActive (filtered server-side before sending), createdAt, updatedAt
 */
export const pricingPlanDisplaySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  basePrice: z.string(), // Decimal string
  billingCycle: z.enum(["monthly", "yearly"]),
  maxUsers: z.number().int().nullable(),
  maxProjects: z.number().int().nullable(),
  maxStorage: z.number().int().nullable(),
  features: z.array(z.string()).default([]),
  isPopular: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
});

// ============================================================
// STAGE-1B: BILLING VALIDATORS
// ============================================================

/**
 * Invoice line item form DTO.
 * Omits: id, createdAt (server-set)
 * NOTE: Invoice creation itself is SERVER_ONLY (stripeInvoiceId, stripeAccountId)
 */
export const invoiceLineItemFormSchema = z.object({
  invoiceId: z.number().int().positive(),
  serviceId: z.number().int().positive().optional(),
  description: z.string().min(1),
  quantity: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/)
    .default("1.00"),
  unitPrice: z.string().regex(/^\d+(\.\d{1,2})?$/),
  lineTotal: z.string().regex(/^\d+(\.\d{1,2})?$/),
  serviceDate: z.string().datetime().optional(),
  technician: z.string().optional(),
  notes: z.string().optional(),
});

// ============================================================
// PEST IDENTIFICATION FORM SCHEMA (Stage-3A)
// ============================================================

/**
 * Pest identification submission form DTO.
 * Browser-safe extraction of insertPestIdentificationSchema.
 * Omits: id, createdAt (server-set), identifiedPest, confidence, recommendations (AI-set)
 * Source table: pestIdentifications in shared/schema.ts
 * Extraction category: Stage-3A Phase D — resolves the single remaining migration blocker
 */
export const createPestIdentificationFormSchema = z.object({
  images: z.array(z.string()),
  location: z.string().min(1),
  brand: z.enum(["aegis360", "greenshield"]),
  size: z.string().min(1),
});

// ============================================================
// TECHNICIAN ASSESSMENT FORM SCHEMA (Stage-3A)
// ============================================================

/**
 * Technician assessment form DTO.
 * Browser-safe extraction of insertTechnicianAssessmentSchema.
 * Omits: id, createdAt, updatedAt (server-set)
 * Source table: technicianAssessments in shared/schema.ts
 * Extraction category: Stage-3A Phase G — eliminates final browser bundle Drizzle contamination
 */
export const createTechnicianAssessmentFormSchema = z.object({
  customerName: z.string(),
  customerEmail: z.string().optional(),
  customerPhone: z.string(),
  propertyAddress: z.string(),
  propertyType: z.string(),
  propertySize: z.string().optional(),
  constructionYear: z.string().optional(),
  numberOfFloors: z.string().optional(),
  basementPresent: z.boolean().default(false),
  atticPresent: z.boolean().default(false),
  garagePresent: z.boolean().default(false),
  pestType: z.array(z.string()),
  infestationSeverity: z.string(),
  affectedAreas: z.array(z.string()),
  previousTreatments: z.string().optional(),
  moistureIssues: z.boolean().default(false),
  foodSources: z.array(z.string()),
  entryPoints: z.array(z.string()),
  landscaping: z.string().optional(),
  healthRisks: z.array(z.string()),
  structuralRisks: z.array(z.string()),
  urgencyLevel: z.string(),
  recommendedTreatment: z.string(),
  followUpRequired: z.boolean().default(false),
  estimatedCost: z.string().optional(),
  technicianName: z.string(),
  technicianId: z.string(),
  assessmentDate: z.string(),
  notes: z.string().optional(),
  status: z.string().default("draft"),
});

// ============================================================
// BLOCKED SCHEMAS (documented for parity — not extractable)
// ============================================================
//
// These schemas CANNOT be extracted to the browser layer because they depend
// on Drizzle table objects or drizzle-zod utilities:
//
// BLOCKED: updateRoleSchema
//   Reason: depends on insertRoleSchema.partial() → insertRoleSchema uses
//           createInsertSchema(roles) which requires the Drizzle `roles` table
//
// BLOCKED: updatePermissionSchema
//   Reason: depends on insertPermissionSchema.partial() → same reason
//
// BLOCKED: all 131 insertXSchema constants
//   Reason: createInsertSchema(table) requires the Drizzle table object from
//           runtime layer (pgTable definition). Cannot be replicated in browser
//           without manually rewriting the Zod schema for each table.
//
// SERVER_ONLY (billing secrets — never extract):
//   insertInvoiceSchema        — stripeInvoiceId, stripePaymentIntentId, stripeAccountId
//   insertPaymentSchema        — Stripe payment internals
//   insertSubscriptionSchema   — stripeCustomerId, stripeSubscriptionId
//   upsertUserSchema           — passwordHash
//   insertIntegrationConfig    — API keys
//
// REMEDIATION PATH (Stage 2+):
//   For remaining LOW-effort blocked schemas, create hand-written equivalents:
//   export const insertUserFormSchema = z.object({
//     email: z.string().email(),
//     firstName: z.string().min(1).optional(),
//     // ... fields that are valid for browser use only
//   });
//   Note: Server-side MUST still re-validate with insertUserSchema (runtime).
